import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@bednbody/ui/components/sidebar";
import { Badge } from "@bednbody/ui/components/badge";
import { NavLink } from "react-router-dom";
import { useKycPendingCount } from "@/features/kyc/kyc.hooks";
import { SupportNavBadge } from "@/features/support/SupportNavBadge";
import type { StaffRole } from "@/lib/jwt-claims";
import { navItemsFor, SECTION_ORDER } from "@/lib/permissions";
import { AppBrand } from "./AppBrand";
import { NavUser } from "./NavUser";

interface AppSidebarProps {
  role: StaffRole;
  pathname: string;
  email: string | null;
  signOut: () => Promise<void>;
}

export function AppSidebar({ role, pathname, email, signOut }: AppSidebarProps) {
  const visibleItems = navItemsFor(role);
  // Only superadmin ever sees the "ID Verification" item at all (permissions.ts),
  // so the query is gated the same way — no point asking a role RLS refuses.
  const kycPendingCount = useKycPendingCount(role === "superadmin");

  return (
    <Sidebar collapsible="icon" variant="floating">
      <SidebarHeader>
        <AppBrand />
      </SidebarHeader>

      <SidebarContent>
        {SECTION_ORDER.map((section) => {
          const items = visibleItems.filter((item) => item.section === section);
          if (items.length === 0) return null;
          return (
            <SidebarGroup key={section}>
              <SidebarGroupLabel>{section}</SidebarGroupLabel>
              <SidebarMenu>
                {items.map(({ to, label, icon: Icon }) => (
                  <SidebarMenuItem key={to}>
                    <SidebarMenuButton
                      isActive={to === "/" ? pathname === "/" : pathname.startsWith(to)}
                      tooltip={label}
                      render={<NavLink to={to} end={to === "/"} />}
                    >
                      <Icon size={18} weight="fill" />
                      <span>{label}</span>
                      {to === "/support" ? <SupportNavBadge pathname={pathname} /> : null}
                      {to === "/kyc" && kycPendingCount > 0 ? (
                        <Badge className="ml-auto" variant="warning">
                          {kycPendingCount > 99 ? "99+" : kycPendingCount}
                        </Badge>
                      ) : null}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter>
        <NavUser email={email} role={role} signOut={signOut} />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
